import { useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { DataGrid, type DataGridColumn } from '@/components/tables/DataGrid';
import { approvalRequestsApi } from '@/api/services/approvalRequests';
import {
  ApprovalRequestCategory,
  type ApprovalRequestRowDto,
} from '@/api/types/approvalRequests';
import { buildRequestColumns, statusBadgeClass } from './columns';

interface HistoryTabProps {
  category: ApprovalRequestCategory;
  queryKey: readonly unknown[];
}

export function HistoryTab({ category, queryKey }: HistoryTabProps) {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey,
    queryFn: () => approvalRequestsApi.getForDataTable({ category, history: true }),
  });

  const rows = query.data?.items ?? [];

  const columns = useMemo<DataGridColumn<ApprovalRequestRowDto>[]>(() => {
    const cols = buildRequestColumns(category, true);
    const status = cols.find((c) => c.key === 'status');
    if (status) {
      status.cell = (r) => <span className={`badge ${statusBadgeClass(r.status)}`}>{r.status}</span>;
    }
    return cols;
  }, [category]);

  return (
    <DataGrid
      columns={columns}
      data={rows}
      rowKey={(r) => r.id}
      columnSearch
      includeExcel
      excelFileName="History"
      emptyMessage={query.isLoading ? 'Loading…' : 'No data.'}
      toolbarStart={
        <button
          type="button"
          className="btn btn-sm btn-outline-primary"
          title="Refresh"
          onClick={() => void queryClient.invalidateQueries({ queryKey })}
        >
          <i className="fa fa-refresh" />
        </button>
      }
    />
  );
}
